/**
 * Environment presets for SFCC Commerce API configuration.
 *
 * Set SFCC_ENV in .dev.vars (or wrangler.toml) to switch environments.
 * Format: {site}-{instance}  e.g. "hsa-development", "fsa-staging"
 *
 * Secrets (SFCC_SCAPI_CLIENT_ID, SFCC_SCAPI_CLIENT_SECRET, SFCC_OCAPI_CLIENT_ID,
 * SFCC_OCAPI_CLIENT_SECRET) stay in .dev.vars — they're per-realm and should
 * never be committed.
 */

// -- Realm definitions (from hec-composable/config/sub-configs/SFCC.js) ------

interface Realm {
  realmKey: string;
  shortCode: string;
  instances: Record<string, string>; // instance name → slug
}

const REALMS: Record<string, Realm> = {
  bfkw: {
    realmKey: "bfkw",
    shortCode: "xdaex6ru",
    instances: { development: "dev", staging: "stg", production: "prd" },
  },
  bkbf: {
    realmKey: "bkbf",
    shortCode: "7yi60ftr",
    instances: { development: "dev", staging: "stg", production: "prd" },
  },
};

// -- Constructor.io API keys (from hec-composable/config/sub-configs/CONSTRUCTOR.js) --
// Public keys (key_...) — safe to ship in the client bundle; these are CIO
// "API Keys" used for browse/search/recs, not the private "API Token" used
// for catalog management.

const CIO_KEYS: Record<string, Record<string, string>> = {
  FSASTORE: {
    development: "key_kWFGqMuw8gvfhv87",
    staging: "key_8Iofrj8DigJ1tVYE",
    production: "key_17zGwGLulbM2W6bY",
  },
  HSASTORE: {
    development: "key_EfubaV5SGkeUmqIS",
    staging: "key_gM50YRTMw0iMt84Q",
    production: "key_H1xqaCSKa0G5CE1A",
  },
  WDH: {
    development: "key_pEgGGzc1J6TovrFW",
    staging: "key_NHJniMSgXjvB1xnk",
    production: "key_037CwZCOGObC7k9p",
  },
};

// -- GTM container IDs (from hec-composable/config/sub-configs/TAG_MANAGER.js) -
// Public container IDs — safe to ship in the client bundle. SFCC-11006
// (ANALYTICS-01). Dev + staging share the non-prod container; production is a
// separate live container.

// Telehealth intake analytics ingest gateway (ET-251). Not per-site: one APIM
// gateway fronts all three storefronts, distinguished downstream by `form_name`
// + the identity block rather than by endpoint.
//
// No `staging` entry (PR #1191 review): MarTech has named only two gateways
// (Gopinath) — this dev one and the prod one below (the prod host is
// `enterprise-telehealth-prod-apim`, confirmed by Gopinath 2026-10-13; the
// spec §1 host `telehealth-prod-apim` resolves but is not the live
// gateway). These are only the FALLBACK: the `apimIngestEndpoint` BM site
// preference is the switch (see `ingest-endpoint.server.ts`), as in the SFRA
// checkout emitter. Staging has no entry here, but its preference is the dev
// gateway, so staging does send there — events interleave with dev, accepted
// for parity with SFRA (the payload has no environment discriminator).
const APIM_INGEST_URLS: Record<string, string> = {
  development: "https://enterprise-telehealth-dev-apim.azure-api.net/ingest",
  production: "https://enterprise-telehealth-prod-apim.azure-api.net/ingest",
};

const GTM_CONTAINER_IDS: Record<string, Record<string, string>> = {
  FSASTORE: {
    development: "GTM-K4TL22P",
    staging: "GTM-K4TL22P",
    production: "GTM-M7ZX4P3",
  },
  HSASTORE: {
    development: "GTM-NC3JVBC",
    staging: "GTM-NC3JVBC",
    production: "GTM-P2C8W33",
  },
  WDH: {
    development: "GTM-5KLNLHP",
    staging: "GTM-5KLNLHP",
    production: "GTM-KG8PNNF",
  },
};

// -- Annex Learning Center action group (from hec-composable/config/sub-configs/ANNEX_CLOUD.js) --
// No BM pref holds it (SFRA has none), so it is per instance here. Only HSA
// runs the Learning Center player on Workers; a missing entry skips the award.
const LEARNING_CENTER_GROUP_IDS: Record<string, Record<string, string>> = {
  HSASTORE: {
    development: "212",
    staging: "212",
    production: "220",
  },
};

export const learningCenterGroupId = (
  preset: Pick<EnvironmentPreset, "siteId" | "instance">,
): string | undefined =>
  LEARNING_CENTER_GROUP_IDS[preset.siteId]?.[preset.instance];

// -- Bloomreach (Exponea) client tracking (SFCC-11019 / ANALYTICS-34) ---------
// BM-verified per-site values (audit_analytics_gtm_segment.md, top-of-file
// block). `enabled` + `eventTarget` are per-site; the prod-vs-nonprod script
// (and its baked-in Exponea token) is per-instance — see `BLOOMREACH_SCRIPT_*`
// below and `buildPreset`. WDH ships with `BloomreachEnabled=false`.

const BLOOMREACH_CONFIG: Record<
  string,
  { enabled: boolean; eventTarget: string }
> = {
  FSASTORE: {
    enabled: true,
    eventTarget: "https://analytics-api.fsastore.com",
  },
  HSASTORE: {
    enabled: true,
    eventTarget: "https://analytics-api.hsastore.com",
  },
  // Disabled — empty eventTarget so a future read can't accidentally point WDH
  // at a live endpoint. The BM-verified configured-but-off value is
  // `https://analytics-api.welldeservedhealth.com` (see audit), kept here as a
  // comment only.
  WDH: { enabled: false, eventTarget: "" },
};

// Static snippet files copied verbatim from SFRA
// (org_hec/.../static/default/js/thirdParty/). Served at root from `public/`.
// Prod and non-prod differ only by the hardcoded Exponea token inside the file.
const BLOOMREACH_SCRIPT_PROD = "/js/thirdParty/bloomreachTrackingProd.js";
const BLOOMREACH_SCRIPT_NONPROD =
  "/js/thirdParty/bloomreachTrackingNonProduction.js";

// Bloomreach (Exponea) transactional-email project ID (SFCC-11515). Per-INSTANCE,
// not per-site — dev+staging share one project, prod another, identical across
// FSA/HSA/WDH (the per-site distinction is the `br_integration_id` site pref).
// Values from hec-composable/config/sub-configs/BLOOMREACH_EXPONEA.js.
const BLOOMREACH_PROJECT_ID_PROD = "01271e68-f5cf-11ee-af6e-ca165de06f21";
const BLOOMREACH_PROJECT_ID_NONPROD = "f03685b2-f5ce-11ee-8b7a-5e98655b738a";

// -- Builder API keys (from hec-composable/config/sub-configs/BUILDER.js) ----

export type BuilderEnv = "MAIN" | "QA" | "SANDBOX" | "DEVELOPMENT";

export const BUILDER_KEYS: Record<
  string,
  Partial<Record<BuilderEnv, string>>
> = {
  HSA: {
    MAIN: "481825b100e64e7f9a22a215d86e03f1",
    QA: "ae91874c38fa4b5fabe908f17a518e63",
    SANDBOX: "180bd60c9f7c4bd385661b06be0cf52c",
    DEVELOPMENT: "f4287051b628458d926464adb51c9e2d",
  },
  FSA: {
    MAIN: "1e07bd57fd054601b600816e06494849",
    QA: "9ff2b37b36c741b7808814352bb6cb1d",
    SANDBOX: "03565755d9b743a3b35dc37bfb8fd16c",
    DEVELOPMENT: "312a9e53b63440918b50bc70e8fc7e92",
  },
  WDH: {
    MAIN: "8fc5552c679040c1bd35a631e1bc738f",
    QA: "66ee40e5b77d4117a16e9c78ecd4eb6e",
    SANDBOX: "b7290206e17f4351b3e646aebe1cc16d",
  },
};

// -- Instance → Builder env mapping ------------------------------------------

export const INSTANCE_TO_BUILDER_ENV: Record<string, BuilderEnv> = {
  development: "DEVELOPMENT",
  staging: "MAIN",
  production: "MAIN",
};

// -- Site → three-letter code mapping ----------------------------------------

export type SiteCode = "hsa" | "fsa" | "wdh";

const SITE_IDS: Record<string, string> = {
  hsa: "HSASTORE",
  fsa: "FSASTORE",
  wdh: "WDH",
};

export const SITE_ID_TO_CODE: Record<string, SiteCode> = {
  HSASTORE: "hsa",
  FSASTORE: "fsa",
  WDH: "wdh",
};

export const SITE_NAMES: Record<SiteCode, string> = {
  hsa: "HSA Store",
  fsa: "FSA Store",
  wdh: "Well Deserved Health",
};

/**
 * Site display name for a SCAPI/BM site ID (`FSASTORE` -> `FSA Store`).
 *
 * These three strings are byte-identical to the `<name>` element in each site's
 * SFRA import XML (`sfcc-website/migrations/*\/sites/<ID>/site.xml`), which is
 * the value SFCC returns from `Site.current.name`. SFRA falls back to exactly
 * that for GA4 `item_brand` when a product carries no brand
 * (`dataLayerHelper.js:455-461`) and for `affiliation`, so resolving through
 * here keeps both stacks writing the SAME brand string into the same GA4
 * property. Returns `undefined` for an unrecognized id rather than guessing.
 *
 * SFCC-12175 W2.
 */
export const siteNameFromSiteId = (
  siteId: string | undefined | null,
): string | undefined => {
  const code = siteId ? SITE_ID_TO_CODE[siteId] : undefined;
  return code ? SITE_NAMES[code] : undefined;
};

/**
 * Resolve a worker's own site code from the `SITE_OVERRIDE` env var that every
 * per-site Worker is deployed with (`--var SITE_OVERRIDE:<site>`; see the
 * deploy-*.sh scripts / `worker.server.ts:resolveSite`). Used by the scheduled
 * redirect-bundle / offline-category syncs so each Worker pulls only its own
 * same-zone site instead of fetching all three — cross-zone fetches to a
 * sibling storefront zone get bot-challenged. Returns `undefined` when unset
 * (local dev / the legacy unified worker) so callers fall back to all sites.
 */
export function resolveSiteCodeFromOverride(
  override?: string | null,
): SiteCode | undefined {
  const code = override?.trim().toLowerCase();
  if (code && code in SITE_NAMES) return code as SiteCode;
  return undefined;
}

export const SITE_TO_BUILDER_SPACE: Record<string, string> = {
  hsa: "HSA",
  fsa: "FSA",
  wdh: "WDH",
};

// -- Image host per site × instance ------------------------------------------
// Non-production uses {site}.devhec.com; production uses the live domain.

export const IMAGE_HOSTS: Record<string, Record<string, string>> = {
  hsa: {
    development: "https://hsa.devhec.com",
    staging: "https://stg.hsastore.com",
    production: "https://www.hsastore.com",
  },
  fsa: {
    development: "https://fsa.devhec.com",
    staging: "https://stg.fsastore.com",
    production: "https://www.fsastore.com",
  },
  wdh: {
    development: "https://wdh.devhec.com",
    staging: "https://stg.welldeservedhealth.com",
    production: "https://www.welldeservedhealth.com",
  },
};

// -- Preset type -------------------------------------------------------------

export interface EnvironmentPreset {
  siteId: string;
  organizationId: string;
  shortCode: string;
  imagesHost: string;
  /**
   * SFCC Dynamic Imaging Service (DIS) image folder id — the `{realm}_{slug}`
   * segment (e.g. `BFKW_PRD` in prod, `BFKW_DEV` on the dev instance) injected
   * into `/dw/image/v2/{id}/…` so transform params are honoured. Must match the
   * serving instance. See `toDisImagePath`.
   */
  imageFolderId: string;
  builderApiKey: string;
  cioApiKey: string;
  /** GTM container ID per site × instance (SFCC-11006). Empty string when unmapped. */
  gtmContainerId: string;
  /**
   * Azure APIM `/ingest` endpoint for telehealth intake funnel analytics
   * (ET-251). Prod and non-prod are separate gateways per the MarTech backend
   * integration spec §1. Empty string disables the emitter entirely — the
   * non-prod gateway is still being stood up, so an empty value must degrade to
   * a no-op rather than throw.
   */
  apimIngestUrl: string;
  /** Bloomreach (Exponea) client tracking enabled for this site (SFCC-11019). */
  bloomreachEnabled: boolean;
  /** Bloomreach Exponea API endpoint (`target`) per site (SFCC-11019). */
  bloomreachEventTarget: string;
  /** Path to the prod/non-prod Bloomreach snippet, selected per instance (SFCC-11019). */
  bloomreachScriptPath: string;
  /** Bloomreach Exponea transactional-email project ID, per instance (SFCC-11515). */
  bloomreachProjectId: string;
  /** The DX host without protocol, for proxy configs */
  dxHost: string;
  /** Resolved instance: "development" | "staging" | "production". Single source
   * of truth for env-specific branches (e.g. prod-vs-stg LinkTrust IDs) instead
   * of re-reading the raw `SFCC_INSTANCE` env var at call sites. */
  instance: string;
}

// -- Preset builder ----------------------------------------------------------

function buildPreset(
  site: string,
  realm: string,
  instance: string,
): EnvironmentPreset {
  const realmData = REALMS[realm];
  if (!realmData)
    throw new Error(`Unknown realm "${realm}" in SFCC_ENV config`);

  const slug = realmData.instances[instance];
  if (!slug)
    throw new Error(
      `Unknown instance "${instance}" for realm "${realm}" in SFCC_ENV config`,
    );

  const siteId = SITE_IDS[site];
  if (!siteId) throw new Error(`Unknown site "${site}" in SFCC_ENV config`);

  const builderSpace = SITE_TO_BUILDER_SPACE[site];
  const builderEnv = INSTANCE_TO_BUILDER_ENV[instance] ?? "DEVELOPMENT";
  // Not every site has every Builder env — e.g. WDH ships only MAIN / QA /
  // SANDBOX (no DEVELOPMENT). Cascade so a missing key falls through to
  // SANDBOX, then MAIN, instead of producing an empty string that crashes
  // the Builder SDK at first content fetch.
  const builderApiKey =
    BUILDER_KEYS[builderSpace]?.[builderEnv] ??
    BUILDER_KEYS[builderSpace]?.SANDBOX ??
    BUILDER_KEYS[builderSpace]?.MAIN ??
    "";

  const cioApiKey = CIO_KEYS[siteId]?.[instance] ?? "";

  const gtmContainerId = GTM_CONTAINER_IDS[siteId]?.[instance] ?? "";

  const apimIngestUrl = APIM_INGEST_URLS[instance] ?? "";

  // Bloomreach: enabled + eventTarget are per-site; the script file (with its
  // baked-in Exponea token) is per-instance, mirroring SFRA's `instanceEnv`
  // check in `scripts.isml:108`. Unmapped sites default to disabled.
  const bloomreach = BLOOMREACH_CONFIG[siteId] ?? {
    enabled: false,
    eventTarget: "",
  };
  const bloomreachScriptPath =
    instance === "production"
      ? BLOOMREACH_SCRIPT_PROD
      : BLOOMREACH_SCRIPT_NONPROD;
  const bloomreachProjectId =
    instance === "production"
      ? BLOOMREACH_PROJECT_ID_PROD
      : BLOOMREACH_PROJECT_ID_NONPROD;

  const dxHost = `${realmData.realmKey}-${slug}.dx.commercecloud.salesforce.com`;
  const imagesHost = IMAGE_HOSTS[site]?.[instance] ?? `https://${dxHost}`;

  // DIS folder id is `{realm}_{slug}` (e.g. `BFKW_PRD` in prod, `BFKW_DEV` on the
  // dev instance) — SFCC's imaging service requires it to match the serving
  // instance, so derive it from the same realm/slug as `dxHost`/`organizationId`.
  // Every instance routes `/dw/image/v2/{id}/*` to the SFCC origin (verified on
  // devhec + all three prod hosts), so no env gating is needed; a mismatched id
  // (e.g. BFKW_PRD against the dev host) 404s.
  const imageFolderId = `${realmData.realmKey}_${slug}`.toUpperCase();

  return {
    siteId,
    organizationId: `f_ecom_${realmData.realmKey}_${slug}`,
    shortCode: realmData.shortCode,
    imagesHost,
    imageFolderId,
    builderApiKey,
    cioApiKey,
    gtmContainerId,
    apimIngestUrl,
    bloomreachEnabled: bloomreach.enabled,
    bloomreachEventTarget: bloomreach.eventTarget,
    bloomreachScriptPath,
    bloomreachProjectId,
    dxHost,
    instance,
  };
}

// -- Pre-built environment map -----------------------------------------------
// Format: {site}-{instance} where instance maps to a realm instance.
// All entries below use the BFKW realm (primary HEC realm).

export const environments: Record<string, EnvironmentPreset> = {
  // HSA
  "hsa-development": buildPreset("hsa", "bfkw", "development"),
  "hsa-staging": buildPreset("hsa", "bfkw", "staging"),
  "hsa-production": buildPreset("hsa", "bfkw", "production"),

  // FSA
  "fsa-development": buildPreset("fsa", "bfkw", "development"),
  "fsa-staging": buildPreset("fsa", "bfkw", "staging"),
  "fsa-production": buildPreset("fsa", "bfkw", "production"),

  // WDH
  "wdh-development": buildPreset("wdh", "bfkw", "development"),
  "wdh-staging": buildPreset("wdh", "bfkw", "staging"),
  "wdh-production": buildPreset("wdh", "bfkw", "production"),

  // BKBF realm (ephemeral/test sandboxes). imagesHost is pinned per entry:
  // IMAGE_HOSTS is keyed site×instance (no realm dimension), so buildPreset
  // would pair the BFKW dev host ({site}.devhec.com) with the BKBF_DEV DIS
  // folder id — a mismatch the BFKW origin 404s (see imageFolderId comment
  // above). The tier's own zone routes /dw/image/v2/* to the bkbf origin.
  "hsa-dev-test": {
    ...buildPreset("hsa", "bkbf", "development"),
    imagesHost: "https://hsatest.devhec.com",
  },
  "fsa-dev-test": {
    ...buildPreset("fsa", "bkbf", "development"),
    imagesHost: "https://fsatest.devhec.com",
  },
  "wdh-dev-test": {
    ...buildPreset("wdh", "bkbf", "development"),
    imagesHost: "https://wdhtest.devhec.com",
  },

  // BKBF PRODUCTION instance — the load-test tier (`[env.prod-test]`,
  // loadtest.devhec.com; see wrangler.toml). Only FSA has a Worker today
  // (`fsa-test-production`), but all three presets exist so the tier can't
  // throw on an unexpected site. imagesHost is pinned for the same
  // realm-mismatch reason as dev-test above: FSA to the tier's own host
  // (its zone routes /dw/image/v2/* to the bkbf-prd origin), HSA/WDH to the
  // bkbf-prd DX host since they have no tier hostname. Per-instance keys
  // (CIO/GTM/Builder MAIN) resolve to the PRODUCTION values — matching the
  // bkbf-prd SFRA site; JS-executing load tests hit the production CIO index.
  "fsa-prod-test": {
    ...buildPreset("fsa", "bkbf", "production"),
    imagesHost: "https://loadtest.devhec.com",
  },
  "hsa-prod-test": {
    ...buildPreset("hsa", "bkbf", "production"),
    imagesHost: "https://bkbf-prd.dx.commercecloud.salesforce.com",
  },
  "wdh-prod-test": {
    ...buildPreset("wdh", "bkbf", "production"),
    imagesHost: "https://bkbf-prd.dx.commercecloud.salesforce.com",
  },
};

/**
 * Resolve an environment preset by site code + instance.
 * This is the multi-site contract: site comes from the request hostname,
 * instance comes from the SFCC_INSTANCE env var.
 *
 * @param site - site code, e.g. "hsa" / "fsa" / "wdh"
 * @param instance - instance, e.g. "development" / "staging" / "production"
 */
export function resolveEnvironmentForSite(
  site: SiteCode,
  instance: string,
): EnvironmentPreset {
  const key = `${site}-${instance}`;
  const preset = environments[key];
  if (!preset) {
    const available = Object.keys(environments).join(", ");
    throw new Error(
      `Unknown environment preset "${key}" (site=${site}, instance=${instance}). Available: ${available}`,
    );
  }
  return preset;
}

/**
 * @deprecated Use resolveEnvironmentForSite(site, instance) instead.
 * Site is now derived from the request hostname (worker.server.ts:resolveSite),
 * not from a composite SFCC_ENV value.
 */
export function resolveEnvironment(sfccEnv: string): EnvironmentPreset {
  const preset = environments[sfccEnv];
  if (!preset) {
    const available = Object.keys(environments).join(", ");
    throw new Error(`Unknown SFCC_ENV "${sfccEnv}". Available: ${available}`);
  }
  return preset;
}

/**
 * @deprecated Replaced by request-scoped preset resolution.
 * `app.server.ts` now reads site from `worker.server.ts` (hostname-derived) and
 * instance from `env.SFCC_INSTANCE`, then calls `resolveEnvironmentForSite()`.
 *
 * This function is kept as a transitional shim — it still writes to
 * `globalThis.__SFCC_PRESET__` so legacy `getPreset()` consumers don't break
 * mid-migration. Will be removed once all consumers read preset from the
 * request context.
 */
export function initPreset(env: Record<string, string>): EnvironmentPreset {
  const sfccEnv = env.SFCC_ENV;
  if (!sfccEnv) {
    const available = Object.keys(environments).join(", ");
    throw new Error(
      `SFCC_ENV is not set. Set it in .dev.vars / wrangler config to one of: ${available}`,
    );
  }
  const preset = resolveEnvironment(sfccEnv);
  (globalThis as Record<string, unknown>).__SFCC_PRESET__ = preset;
  (globalThis as Record<string, unknown>).__SFCC_SCAPI_CLIENT_ID__ =
    env.SFCC_SCAPI_CLIENT_ID ?? undefined;
  (globalThis as Record<string, unknown>).__SFCC_OCAPI_CLIENT_ID__ =
    env.SFCC_OCAPI_CLIENT_ID ?? undefined;
  return preset;
}
